import { usePathname } from 'expo-router';
import { useEffect, useRef } from 'react';

/** Fields that take focus on purpose; a page that focused one on arrival keeps it. */
const EDITABLE_FOCUS = 'input, textarea, select, [contenteditable="true"]';

/**
 * A page's heading can render after its data, so the search retries for up to two seconds. It
 * gives up as soon as something other than the page body takes focus, so it never pulls focus
 * away from a reader who has already moved on.
 */
const ATTEMPTS = 40;

const ATTEMPT_INTERVAL_MS = 50;

function isOnPage(heading: HTMLElement) {
  return !heading.closest('[data-navbar]') && heading.getClientRects().length > 0;
}

/**
 * The page's level-one heading, an `h1` or a text given that role, or its first `h2`/`h3` when it
 * has none, as on a gate or a sheet.
 */
function visibleMainHeading(): HTMLElement | undefined {
  return (
    Array.from(document.querySelectorAll<HTMLElement>('h1, [role="heading"][aria-level="1"]')).find(
      isOnPage
    ) ?? Array.from(document.querySelectorAll<HTMLElement>('h2, h3')).find(isOnPage)
  );
}

/**
 * Moves keyboard focus to the new page's main heading after each in-app navigation.
 *
 * Without it, focus stays on the link that was pressed, so a keyboard user starts the new page from
 * the nav bar and a screen reader announces nothing. Focusing the heading both moves them and
 * makes the reader announce the page, which tested better than a live-region announcement alone.
 * The first page of a load is left alone, where the browser already starts at the top, and so is
 * a page that focused a field on arrival. Screens kept mounted under the current one are hidden,
 * so only a visible heading counts.
 */
export function useRouteChangeFocus() {
  const pathname = usePathname();

  const isFirstPage = useRef(true);

  useEffect(() => {
    if (isFirstPage.current) {
      isFirstPage.current = false;

      return;
    }

    let attempts = 0;

    const focusAtNavigation = document.activeElement;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const focusHeading = () => {
      const active = document.activeElement;

      if (active?.matches(EDITABLE_FOCUS)) {
        return;
      }

      if (active && active !== document.body && active !== focusAtNavigation) {
        return;
      }

      const heading = visibleMainHeading();

      if (!heading) {
        attempts += 1;

        if (attempts < ATTEMPTS) {
          timer = setTimeout(focusHeading, ATTEMPT_INTERVAL_MS);
        }

        return;
      }

      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
      }

      heading.focus({ preventScroll: true });
    };

    timer = setTimeout(focusHeading, 0);

    return () => clearTimeout(timer);
  }, [pathname]);
}
